import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { api } from '../../context/AuthContext';
import { Loading, Section, StatusBadge, errorText } from './shared';

const entryStatus = (entry) => {
  if (!entry.configured) return 'not_configured';
  if (entry.auto_disabled) return 'auto_disabled';
  return entry.health?.status || 'unknown';
};

// Only the fields the API stores
function strip(entry) {
  const out = { provider: entry.provider, model: entry.model, enabled: entry.enabled !== false };
  ['params', 'deprecation_date', 'note'].forEach((k) => {
    if (entry[k] && (typeof entry[k] !== 'object' || Object.keys(entry[k]).length)) out[k] = entry[k];
  });
  return out;
}

const entryDetails = (entry) => [
  entry.health?.deprecation_date && `Retires ${entry.health.deprecation_date}`,
  entry.health?.probe?.ok && `Probe ${entry.health.probe.latency_ms} ms`,
  entry.note,
].filter(Boolean).join(' · ');

const ChainRow = ({ entry, index, total, onChange, onMove, onRemove, onReEnable }) => (
  <li className="grid grid-cols-[2rem_1fr] items-center gap-x-3 gap-y-2 px-4 py-3 sm:grid-cols-[2rem_1fr_auto_auto]">
    <span className="text-sm tabular-nums text-slate-400">{index + 1}</span>
    <div className="min-w-0">
      <p className="truncate text-sm font-medium text-slate-800" title={`${entry.provider}/${entry.model}`}>
        <span className="text-slate-500">{entry.provider}/</span>{entry.model}
      </p>
      {entryDetails(entry) && <p className="truncate text-xs text-slate-500">{entryDetails(entry)}</p>}
    </div>
    <div className="col-start-2 flex items-center gap-2 sm:col-start-auto">
      <StatusBadge status={entryStatus(entry)} />
      {entry.auto_disabled && <Button size="sm" variant="outline" className="h-7" onClick={onReEnable}>Re-enable</Button>}
    </div>
    <div className="col-start-2 flex items-center gap-1 sm:col-start-auto">
      <Switch checked={entry.enabled !== false} onCheckedChange={(v) => onChange({ enabled: v })}
        aria-label={`Enable ${entry.model}`} className="mr-2" />
      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Move up">
        <ArrowUp className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={index === total - 1} onClick={() => onMove(1)} aria-label="Move down">
        <ArrowDown className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onRemove} aria-label="Remove">
        <Trash2 className="h-4 w-4 text-red-500" />
      </Button>
    </div>
  </li>
);

const ProfileCard = ({ name, profile, providers, onSaved }) => {
  const [chain, setChain] = useState(profile.chain);
  const [adding, setAdding] = useState({ provider: '', model: '' });
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(chain.map(strip)) !== JSON.stringify(profile.chain.map(strip));

  useEffect(() => setChain(profile.chain), [profile.chain]);

  const move = (index, delta) => setChain((prev) => {
    const next = [...prev];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    return next;
  });
  const update = (index, patch) => setChain((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  const eligible = Object.entries(providers).filter(([, p]) => p.capabilities.includes(profile.capability));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/ai/profiles/${name}`, { chain: chain.map(strip) });
      toast.success(`${name} saved. New requests use it within a minute.`);
      onSaved();
    } catch (error) {
      toast.error(errorText(error, 'Could not save the chain'));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${name} to the default chain from the code?`)) return;
    try {
      await api.delete(`/admin/ai/profiles/${name}`);
      toast.success(`${name} reset to default`);
      onSaved();
    } catch (error) {
      toast.error(errorText(error, 'Could not reset'));
    }
  };

  const reEnable = async (entry) => {
    try {
      await api.post('/admin/ai/models/enable', { provider: entry.provider, model: entry.model });
      toast.success(`${entry.provider}/${entry.model} re-enabled`);
      onSaved();
    } catch (error) {
      toast.error(errorText(error, 'Could not re-enable'));
    }
  };

  const addEntry = () => {
    setChain((prev) => [...prev, {
      provider: adding.provider, model: adding.model.trim(), enabled: true, health: {},
      configured: providers[adding.provider]?.configured,
    }]);
    setAdding({ provider: '', model: '' });
  };

  return (
    <Section
      title={(
        <span className="flex flex-wrap items-center gap-2">
          <code className="text-sm">{name}</code>
          <Badge variant="secondary" className="font-normal">{profile.capability}</Badge>
          {profile.overridden && <Badge className="bg-indigo-50 font-normal text-indigo-700 hover:bg-indigo-50">customised</Badge>}
          {dirty && <Badge className="bg-amber-50 font-normal text-amber-800 hover:bg-amber-50">unsaved</Badge>}
        </span>
      )}
      description="Tried top to bottom. Models without a key, switched off or failing are skipped."
      actions={(
        <>
          {profile.overridden && (
            <Button size="sm" variant="ghost" onClick={reset}><RotateCcw className="mr-1 h-4 w-4" />Use default</Button>
          )}
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-emerald-500 text-white hover:bg-emerald-600">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save
          </Button>
        </>
      )}
      contentClassName="px-0 pb-0"
      footer={(
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm sm:w-44" value={adding.provider}
            onChange={(e) => setAdding({ ...adding, provider: e.target.value })} aria-label="Provider">
            <option value="">Provider…</option>
            {eligible.map(([id, p]) => <option key={id} value={id}>{id}{p.configured ? '' : ' (no key)'}</option>)}
          </select>
          <Input className="h-9 sm:max-w-md" placeholder="Model id, e.g. google/veo-3.1" value={adding.model}
            onChange={(e) => setAdding({ ...adding, model: e.target.value })} />
          <Button size="sm" variant="outline" disabled={!adding.provider || !adding.model.trim()} onClick={addEntry}>
            <Plus className="mr-1 h-4 w-4" />Add to chain
          </Button>
        </div>
      )}
    >
      <ul className="divide-y divide-slate-100 border-t border-slate-100">
        {chain.map((entry, index) => (
          <ChainRow key={`${entry.provider}/${entry.model}`} entry={entry} index={index} total={chain.length}
            onChange={(patch) => update(index, patch)} onMove={(delta) => move(index, delta)}
            onRemove={() => setChain((prev) => prev.filter((_, i) => i !== index))} onReEnable={() => reEnable(entry)} />
        ))}
      </ul>
    </Section>
  );
};

/** AI routing: per-profile fallback chains with health, editable without a redeploy. */
const RoutingTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get('/admin/ai/profiles').then((res) => setData(res.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const resetCircuit = async (provider) => {
    await api.post('/admin/ai/circuits/reset', null, { params: provider ? { provider } : {} });
    toast.success(`Circuit reset: ${provider || 'all providers'}`);
    load();
  };

  if (!data) return <Loading />;

  return (
    <div className="space-y-6">
      <Section title="Providers"
        description={<>Status from API keys, the last health check and temporary skips. Set <code>AI_PREFERRED_PROVIDER</code> on the
          server to put one provider first in every chain.</>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(data.providers).map(([id, p]) => (
            <div key={id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2.5"
              title={p.account_problem || ''}>
              <span className="text-sm font-medium text-slate-800">{id}</span>
              <div className="flex items-center gap-2">
                <StatusBadge status={!p.configured ? 'not_configured' : p.account_problem ? 'account' : data.circuits?.[id] ? 'failing' : 'ok'} />
                {data.circuits?.[id] && (
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => resetCircuit(id)}>Reset</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>
      {Object.entries(data.profiles).map(([name, profile]) => (
        <ProfileCard key={name} name={name} profile={profile} providers={data.providers} onSaved={load} />
      ))}
    </div>
  );
};

export default RoutingTab;
